import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useFilters } from '../../contexts/FiltersContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useUI } from '../../contexts/UIContext';
import { streamAssistant } from './api';

/**
 * The assistant's state: whether the panel is open, the conversation, and what
 * the user is looking at right now.
 *
 * The conversation lives here, above the routes, so it survives navigation:
 * ask on the Overview, open a contact, and keep going. It is not persisted —
 * a reload starts a fresh chat, which is the honest default until threads are
 * a feature.
 *
 * "Context" is what makes "this call" and "this customer" mean something. It is
 * read fresh at send time from the route, the screen's period, the open call
 * drawer, and whatever a screen registered with `useAssistantScope`.
 */

const AssistantContext = createContext(null);

export const useAssistant = () => {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider');
  return ctx;
};

/** Optional variant for components that may render outside the provider (tests, public pages). */
export const useAssistantOptional = () => useContext(AssistantContext);

/**
 * Tell the assistant what this screen is about while it is mounted, e.g. the
 * contact drawer registers the contact's phone so "draft a follow-up for them"
 * needs no setup. Pass a stable object (memoise it) or primitives.
 */
export const useAssistantScope = (scope) => {
  const ctx = useContext(AssistantContext);
  const register = ctx?.registerScope;
  const key = JSON.stringify(scope || {});
  useEffect(() => {
    if (!register || !scope) return undefined;
    return register(JSON.parse(key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, key]);
};

let nextId = 1;
const newId = () => `m${Date.now().toString(36)}${(nextId++).toString(36)}`;

/**
 * What an assistant turn says about itself in the history sent back next time.
 * The model only sees text, so the cards it produced are summarised in a line —
 * otherwise "approve that" or "the second call" would refer to nothing.
 */
const historyText = (message) => {
  if (message.role === 'user') return message.text;
  const notes = [];
  (message.blocks || []).forEach((b) => {
    if (b.type === 'calls') notes.push(`showed calls ${b.calls.map((c) => c.callId).join(', ')}`);
    if (b.type === 'contact') notes.push(`showed contact ${b.phone}`);
    if (b.type === 'draft') notes.push(`drafted a ${b.channel} message`);
    if (b.type === 'action') notes.push(`proposed "${b.action.title}" (${b.action.status})`);
  });
  return notes.length ? `${message.text || ''}\n\n[${notes.join('; ')}]` : message.text || '';
};

export function AssistantProvider({ children }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { filters, period } = useFilters();
  const { timeZone } = usePreferences();
  const { selectedCall, isModalOpen } = useUI();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const scopes = useRef(new Map());
  const [scopeVersion, setScopeVersion] = useState(0);

  const registerScope = useCallback((scope) => {
    const token = Symbol('scope');
    scopes.current.set(token, scope);
    setScopeVersion((v) => v + 1);
    return () => {
      scopes.current.delete(token);
      setScopeVersion((v) => v + 1);
    };
  }, []);

  /** What the user is looking at, assembled at the moment it is needed. */
  const screenContext = useMemo(() => {
    const onCalls = location.pathname.startsWith('/calls');
    const range = onCalls ? filters : period;
    const params = new URLSearchParams(location.search);
    const ctx = {
      route: location.pathname,
      tz: timeZone,
      start_date: range?.startDate || null,
      end_date: range?.endDate || null,
    };
    if (onCalls) {
      const active = {};
      ['search', 'agent', 'sentiment', 'category', 'answered', 'inbound', 'actionStatus', 'qualityMin', 'qualityMax'].forEach(
        (k) => {
          if (filters?.[k]) active[k] = filters[k];
        }
      );
      if (filters?.drillReason) active.showing = filters.drillReason;
      if (Object.keys(active).length) ctx.filters = active;
    }
    if (location.pathname.startsWith('/contacts') && params.get('phone')) {
      ctx.phone = params.get('phone');
      ctx.contact_name = params.get('name') || null;
    }
    if (location.pathname.startsWith('/performance') && params.get('name')) {
      ctx.agent = params.get('name');
    }
    // On the Outlook, "this week" means the week on screen.
    if (location.pathname.startsWith('/outlook')) {
      ctx.outlook_period = params.get('period') || 'day';
      if (params.get('date')) ctx.outlook_date = params.get('date');
    }
    // Registered scopes are the most specific thing on screen (an open drawer),
    // so they win over what the URL says.
    scopes.current.forEach((scope) => Object.assign(ctx, scope));
    if (isModalOpen && selectedCall) {
      ctx.call_id = selectedCall.callId || selectedCall.id || null;
    }
    return ctx;
    // scopeVersion is the signal that the scope map changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, filters, period, timeZone, isModalOpen, selectedCall, scopeVersion]);

  const contextRef = useRef(screenContext);
  contextRef.current = screenContext;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // The answer being streamed, so Stop and New chat can end it.
  const streamRef = useRef(null);

  /** Apply `change` to one message, by id. */
  const patch = useCallback((id, change) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...change(m) } : m)));
  }, []);

  /** One streamed event onto the assistant message it belongs to. */
  const apply = useCallback(
    (id, event) => {
      if (event.type === 'thinking') patch(id, (m) => ({ thinking: m.thinking + event.delta }));
      else if (event.type === 'text') patch(id, (m) => ({ text: m.text + event.delta }));
      else if (event.type === 'block') patch(id, (m) => ({ blocks: [...m.blocks, event.block] }));
      else if (event.type === 'step') {
        patch(id, (m) => {
          const known = m.steps.some((s) => s.id === event.id);
          return {
            steps: known
              ? m.steps.map((s) => (s.id === event.id ? { ...s, status: event.status } : s))
              : [...m.steps, { id: event.id, label: event.label, status: event.status }],
          };
        });
      } else if (event.type === 'done') {
        patch(id, () => ({
          text: event.text || '',
          blocks: Array.isArray(event.blocks) ? event.blocks : [],
          streaming: false,
          endedAt: Date.now(),
        }));
      } else if (event.type === 'error') {
        patch(id, (m) => ({ error: true, streaming: false, text: event.message, retry: m.question }));
      }
    },
    [patch]
  );

  const send = useCallback(
    async (text) => {
      const question = String(text || '').trim();
      if (!question || pending) return;
      const userMsg = { id: newId(), role: 'user', text: question };
      const history = [...messagesRef.current.filter((m) => !m.error && !m.streaming), userMsg];
      const id = newId();
      // The answer appears at once and fills in as it streams: thinking, steps,
      // cards, then the reply.
      setMessages((prev) => [
        ...prev,
        userMsg,
        { id, role: 'assistant', question, streaming: true, text: '', thinking: '', steps: [], blocks: [], startedAt: Date.now() },
      ]);
      setPending(true);
      const controller = new AbortController();
      streamRef.current = { id, controller };
      let finished = false;
      try {
        await streamAssistant(
          history.map((m) => ({ role: m.role, content: historyText(m) })),
          contextRef.current,
          (event) => {
            if (event.type === 'done' || event.type === 'error') finished = true;
            apply(id, event);
          },
          controller.signal
        );
        if (!finished) throw new Error('The answer was cut off. Try again.');
      } catch (e) {
        if (controller.signal.aborted) {
          // Stopped on purpose: keep what arrived, say it was stopped.
          patch(id, (m) => ({ streaming: false, stopped: true, endedAt: Date.now(), text: m.text }));
        } else {
          patch(id, () => ({ error: true, streaming: false, text: e.message, retry: question }));
        }
      } finally {
        if (streamRef.current?.id === id) streamRef.current = null;
        setPending(false);
      }
    },
    [pending, apply, patch]
  );

  /** Stop the answer being written. What already arrived stays on screen. */
  const stop = useCallback(() => {
    streamRef.current?.controller.abort();
  }, []);

  /** Ask the last failed question again, dropping the failure and its question. */
  const retry = useCallback(
    (messageId) => {
      const failed = messagesRef.current.find((m) => m.id === messageId);
      if (!failed?.retry) return;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        return idx > 0 ? prev.slice(0, idx - 1) : prev.filter((m) => m.id !== messageId);
      });
      // Let the state settle so the resent question is not doubled in history.
      setTimeout(() => send(failed.retry), 0);
    },
    [send]
  );

  /** A card changed state (approved, cancelled, undone): store it and refresh the screens. */
  const updateAction = useCallback(
    (action) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.blocks
            ? {
                ...m,
                blocks: m.blocks.map((b) =>
                  b.type === 'action' && b.action.id === action.id ? { ...b, action } : b
                ),
              }
            : m
        )
      );
      if (action.status === 'executed' || action.status === 'undone') {
        // Whatever screen is behind the panel may be showing what just changed.
        queryClient.invalidateQueries();
      }
    },
    [queryClient]
  );

  const reset = useCallback(() => {
    streamRef.current?.controller.abort();
    setMessages([]);
  }, []);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Drawers read this to stop short of the panel on wide screens (index.css).
  useEffect(() => {
    const root = document.documentElement;
    if (open) root.dataset.assistant = 'open';
    else delete root.dataset.assistant;
    return () => {
      delete root.dataset.assistant;
    };
  }, [open]);

  // ⌘K / Ctrl+K opens and closes the panel from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      messages,
      pending,
      send,
      stop,
      retry,
      reset,
      updateAction,
      screenContext,
      registerScope,
    }),
    [open, toggle, messages, pending, send, stop, retry, reset, updateAction, screenContext, registerScope]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
